/**
 * Page translation must not crash the app.
 *
 * Chrome translate (and Edge, and translate extensions) replaces the text nodes of the page with
 * its own `<font>` elements. React still holds the old text nodes. When React then removes one, or
 * inserts next to one, the browser throws `NotFoundError: Failed to execute 'removeChild' on 'Node'`
 * and the error boundary shows the error screen (Sentry TEAMNA-DESK-WEB-4: a zh-CN browser on the
 * WhatsApp step of `/sign-in`, where the buttons change when the phase changes).
 *
 * The fix is the one from React issue #11538: when the node is not a child of this parent any more,
 * do nothing for `removeChild`, and append for `insertBefore`. A node that is not there is already
 * removed, so this changes nothing for a page that is not translated. Text that React changes after
 * translation can stay in the old words on screen; `Button` keys its text so that labels show the
 * new words (see `translatableText` in components/Button.tsx).
 *
 * Call it once, before the first render.
 */
export function guardDomAgainstTranslation(): void {
  if (typeof Node !== "function" || !Node.prototype) return;
  const proto = Node.prototype as Node & { teamnaTranslateGuard?: true };
  if (proto.teamnaTranslateGuard) return;
  proto.teamnaTranslateGuard = true;

  const removeChild = proto.removeChild;
  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      warnOnce("removeChild", "Nothing removed");
      return child;
    }
    return removeChild.call(this, child) as T;
  };

  const insertBefore = proto.insertBefore;
  proto.insertBefore = function <T extends Node>(this: Node, node: T, reference: Node | null): T {
    if (reference && reference.parentNode !== this) {
      warnOnce("insertBefore", "Added at the end");
      return insertBefore.call(this, node, null) as T;
    }
    return insertBefore.call(this, node, reference) as T;
  };
}

let warned = false;
function warnOnce(call: string, done: string): void {
  if (warned) return;
  warned = true;
  console.warn(`Teamna: ${call} on a node that is no longer in its place (page translation?). ${done}.`);
}
